import React, { useMemo } from 'react';
import { getLocale } from '../../i18n';
import '../PolicyAndServicePopup.global.styl';

function Policy({ visible }: { visible: boolean }) {
  const enContent = useMemo(
    () => (
      <div className="policy-content">
        <h2>Privacy Policy</h2>
        <div style={{ marginBottom: 24 }}>
          <i>Updated August 2, 2021</i>
        </div>
        <p>
          <strong>
            Please read carefully and comprehend this Privacy Policy before
            using our products or services.{' '}
          </strong>
        </p>
        <p>
          <strong>[Preface]</strong> You are welcome to use the products and
          services of MioTech. MioTech place great importance on your privacy
          and undertakes to protect your personal information privacy. Before
          and during your usage of our products and services, we may collect and
          use your certain personal information. Through this Privacy Policy, we
          hope to let you know the processing rules and other related matters
          when we collect and use your personal information, so as to better
          protect your rights and interests.
        </p>
        <p>
          This Privacy Policy covers users of our website{' '}
          <a href="https://www.miotech.com">www.miotech.com</a>, other Internet
          sites and websites of MioTech (collectively referred to as "
          <strong>MioTech Websites</strong>"), AMI, Spreadi and other platforms
          and products of MioTech as well as our other services (together with
          MioTech Websites, "<strong>MioTech Products and Services</strong>").
        </p>
        <p>
          In this Privacy Policy, "<strong>MioTech</strong>", "
          <strong>our company</strong>" and "<strong>we</strong>" refer to
          Yingtou Information Technology (Shanghai) Co., Ltd. and its
          affiliates, while "<strong>user</strong>" and "<strong>you</strong>"
          refer to each user and customer of MioTech Products and Services.
        </p>
        <p>
          <strong className="text-underline">
            [Important Notice] We will update this Privacy Policy from time to
            time and publish it on MioTech Websites. This Privacy Policy is an
            integral part of all MioTech Products and Services agreements. When
            you visit MioTech Websites or use MioTech Products and Services, it
            means that you have agreed to all the content of this Privacy
            Policy; otherwise you should stop visiting MioTech Websites or using
            MioTech Products and Services. We undertake to protect your personal
            information in a secure and transparent manner. We will collect
            personal information for the purpose of providing and improving our
            products and services. Unless otherwise specified in this Privacy
            Policy, we will not use or share your personal information for any
            other purposes.
          </strong>
        </p>
        <p>Table of Content</p>
        <p>This Privacy Policy will help you understand the following:</p>
        <p>I. How we collect and use your personal information</p>
        <p>
          II. How we store, process, share, transfer and disclose your personal
          information
        </p>
        <p>III. How we protect your personal information</p>
        <p>IV. Your right</p>
        <p>V. How your personal information is transferred globally</p>
        <p>VI. Changes to Privacy Policy</p>
        <p>VII. Contact us</p>
        <p>
          We are well aware of the importance of your personal information to
          you and that providing effective protection for your personal
          information is important for the healthy and sustainable development
          of our business. We thank you for your trust in MioTech! We are
          committed to maintaining your trust in us and adhering to this Privacy
          Policy to protect your personal information.{' '}
        </p>
        <p>
          We undertake that we will take corresponding security measures to
          protect your personal information.{' '}
        </p>
        <p>
          <strong>I. How we collect and use your personal information</strong>
        </p>
        <p>
          1. What personal information we will collect from you and how we
          collect them
        </p>
        <p>(1) Information you provide</p>
        <p>
          We will collect the information you share with us when you contact us,
          subscribe our newsletters, purchase MioTech Products and Services,
          create an account, send us an email or share your personal information
          with us through other methods.
        </p>
        <p>
          Depending on your choice, such information may include your name,
          telephone number, email address, company name, company position,
          company address or payment information. At present, telephone
          number/email address must be provided. Telephone number/email address
          is for you to register and log in MioTech Websites and use MioTech
          Products and Services. You need to register through your telephone
          number/email address before you can continue to use our relevant
          services. User's telephone number/email address has various functions
          such as ensuring account security and receiving reminders. Apart from
          telephone number/email address, other information is not mandatorily
          required, but if such information is missing, you will not be able to
          access certain contents, functions or services of us.{' '}
        </p>
        <p>(2) Information you post</p>
        <p>
          Information posted by you in the public areas of MioTech Websites and
          MioTech Products and Services will be collected.{' '}
        </p>
        <p>
          (3) Information formed in the process of providing you with services
        </p>
        <p>
          We and third parties that provide content, advertising, or other
          functionality on MioTech Products and Services, may use cookies, pixel
          tags, local storage, and other technologies to automatically collect
          information through those products and services. We use technologies
          that are essentially small data files placed on your computer, tablet,
          mobile phone, or other devices that allow us to record certain pieces
          of information whenever you visit or interact with our sites,
          services, applications, and to recognize you across devices. Most
          browsers allow you to block and delete cookies. However, if you do
          that, our services may not work properly. We also use third party
          analytics such as Google Analytics to gather and analyze aggregated
          anonymous user information.{' '}
        </p>
        <p>(4) Information from other sources</p>
        <p>
          We may receive information about you from other sources, including
          through third party services and organizations to supplement
          information provided by you. This information allows us to verify
          information that you have provided to MioTech and to enhance our
          ability to provide you with information about our business, products,
          and services.
        </p>
        <p>2. How we use your personal information</p>
        <p>
          (1) We will use your personal information for providing or improving
          our products and services.
        </p>
        <p>
          (2) You authorize us to continuously collect and use your personal
          information while using our products or services. When you deregister
          our services, we will stop collecting your personal information.
        </p>
        <p>
          (3) To improve your product or service experience or prevent risks, we
          will summarize, analyze and process information relating to usage of
          our services, but such information will not include any of your
          identification information.
        </p>
        <p>
          (4) We will also use the above information to maintain and improve our
          product and service functions, develop new functions.
        </p>
        <p>
          (5) We will use your personal information for other purposes permitted
          by law.
        </p>
        <p>
          <strong>
            II. How we store, process, share, transfer and disclose your
            personal information
          </strong>
        </p>
        <p>1. Storage</p>
        <p>
          (1) We will store your personal information in places in accordance
          with relevant laws and regulations.
        </p>
        <p>
          (2) The maximum period we store the relevant data of individual user
          and user logs will be in line with the period required by laws and
          regulations and the period necessary to fulfill the purpose of this
          Privacy Policy.
        </p>
        <p>2. Processing</p>
        <p>
          (1) We may hire third-party companies and individuals to process your
          personal information and share your personal information due to the
          following reasons: (1) to facilitate our providing MioTech Products
          and Services; (2) to provide MioTech Products and Services on our
          behalf; (3) where certain specific modules or functions in our
          products and services are provided by external suppliers; or (4) to
          assist us in analyzing how MioTech Products and Services are used.
        </p>
        <p>
          (2) The said third parties have the right to access your personal
          information because they perform the assigned tasks on behalf of us.
          However, they are bound by obligations not to disclose or use such
          information for any other purpose.
        </p>
        <p>
          (3) We will only share your personal information for legal, proper,
          necessary, specific and clear purposes necessary to provide MioTech
          Products and Services, excluding any information that cannot identify
          specific individuals and cannot be recovered after processing.
        </p>
        <p>
          (4) We will sign confidentiality agreements with companies,
          organizations and individuals with whom we share personal information
          and require them to process personal information according to our
          requirements and any other relevant confidentiality and security
          measures.
        </p>
        <p>3. Transfer</p>
        <p>
          Except for the following circumstances, we will not transfer your
          personal information to any company, organization or individual other
          than our affiliates:
        </p>
        <p>(1) such transfer is clearly specified in this Privacy Policy;</p>
        <p>
          (2) such transfer is to meet the requirements of laws and regulations
          or legal procedures or mandatory government requirements or court
          decisions; or
        </p>
        <p>
          (3) if we or our affiliates are involved in merger, division,
          liquidation, acquisition or sale of assets or business or other
          transactions, your personal information may be transferred as part of
          such transactions. We will ensure the confidentiality of such
          information at the time of transfer, and try our best to ensure that
          the new company or organization holding your personal information
          continues to be bound by this Privacy Policy.
        </p>
        <p>4. Disclosure</p>
        <p>
          We will only disclose your personal information under the following
          circumstances:
        </p>
        <p>(1) clearly specified in this Privacy Policy;</p>
        <p>(2) after obtaining your express consent; or</p>
        <p>
          (3) disclosure according to law: we may disclose your personal
          information when such disclosure is required by law, legal
          proceedings, litigation or mandatory requirements by competent
          government authorities.{' '}
        </p>
        <p>5. Exceptions</p>
        <p>
          Under the following circumstances, your authorization and consent will
          not be required for sharing, transferring or disclosing your personal
          information:
        </p>
        <p>
          (1) when it is directly related to national security and national
          defense security;
        </p>
        <p>
          (2) when it is directly related to public security, public health and
          major public interests;
        </p>
        <p>
          (3) when it is directly related to criminal investigation,
          prosecution, trial and judgment execution;
        </p>
        <p>
          (4) when it is for the purpose of safeguarding your or other’s life,
          property and other major legal rights and interests while it is
          difficult to obtain prior consent;
        </p>
        <p>(5) the personal information was voluntarily disclosed by you; or</p>
        <p>
          (6) personal information collected from legitimate sources, such as
          news reports, government announcement, etc.{' '}
        </p>
        <p>
          <strong>III. How we protect your personal information</strong>
        </p>
        <p>
          1. We will use security measures in line with industry standards to
          protect your personal information from unauthorized access,
          disclosure, usage, modification, damage or loss. We will take all
          reasonable and feasible measures to protect your personal information.
        </p>
        <p>
          2. We value your trust in providing us your Personal Information, thus
          we are striving to use commercially acceptable means of protecting it.
          However, please understand that due to the limitation of technology
          and potential malicious attack, there may be unexpected circumstances
          that we cannot reasonably foresee, prevent, avoid or control. The
          Internet is not an absolutely secure environment. Please use a complex
          password to assist us protecting your account security.
        </p>
        <p>
          3. Our services may contain links to other sites. If you click on a
          third-party link, you will be directed to that site. Note that these
          external sites are not operated by us. Therefore, we strongly advise
          you to review the Privacy Policy of these websites. We have no control
          over and assume no responsibility for the content, privacy policies,
          or practices of any third-party sites or services.
        </p>
        <p>
          4. In case of a personal information security incident, we will timely
          inform you of the relevant information of the incident by email,
          letter, telephone, push notice, etc., or make an announcement in a
          reasonable and effective manner according to the requirements of laws
          and regulations. We will also report the handling of the personal
          information security incident according to relevant regulations.{' '}
        </p>
        <p>
          5. How to process the personal information of minors. Our services do
          not address anyone under the age of 13. We do not knowingly collect
          personally identifiable information from children under 13. In the
          case we discover that a child under 13 has provided us with personal
          information, we immediately delete this from our servers. If you are a
          parent or guardian and you are aware that your child has provided us
          with personal information, please contact us so that we will be able
          to do necessary actions.{' '}
        </p>
        <p>
          <strong>IV. Your right</strong>
        </p>
        <p>
          According to the relevant laws, regulations and standards of People's
          Republic of China and the common practices of other countries and
          regions, we endeavor that you may exercise the following rights in
          relation to your personal information:
        </p>
        <p>1. Access and correction</p>
        <p>
          Unless otherwise required by laws and regulations, you have the right
          to access your personal information. If you want to exercise your
          right of personal data access, please contact us by sending an email
          to info@miotech.com. When you find that there are errors in your
          personal information processed by us, you may require us to make
          corrections. You may contact us and submit the correction application
          by sending an email to info@miotech.com.{' '}
        </p>
        <p>2. Change of authorization</p>
        <p>
          You have the right to object to and opt out of certain uses and
          disclosures of your personal information. Where you have consented to
          MioTech’s processing of your personal information, you may withdraw
          that consent at any time and opt out of further processing by sending
          an email to info@miotech.com. Even if you opt out, we may still
          collect and use non-personal information regarding your activities on
          our websites and/or information from the advertisements on third-party
          websites for non-profit advertising purposes, such as to determine the
          effectiveness of the advertisements.
        </p>
        <p>
          If you receive an unwanted email from us, you can use the unsubscribe
          link found at the bottom of the email to opt out of receiving future
          emails. We will process your request within a reasonable time after
          receipt. Note that you will continue to receive transaction-related
          emails regarding products or services you have requested. We may also
          send you certain non-promotional communications regarding MioTech and
          our services and you will not be able to opt out of those
          communications (e.g., communications regarding updates to our Terms or
          this Privacy Policy).{' '}
        </p>
        <p>3. Deletion</p>
        <p>
          You may request us to delete your personal information under the
          following circumstances:
        </p>
        <p>
          (1) We terminate the operation of MioTech Websites or MioTech Products
          and Services.
        </p>
        <p>
          (2) If you deregister your account with us, we will delete or
          anonymize your personal information.
        </p>
        <p>
          (3) We will delete your personal information if any trade secret is
          concerned.{' '}
        </p>
        <p>4. Opt-out</p>
        <p>
          You may deregister your account registered with us at any time. You
          may contact our customer service personnel to deregister your MioTech
          account. Once you deregister your account, you will not be able to use
          our products and services, so please choose this option with caution.
          To protect your and others’ legal rights and interests, we will
          determine whether to accept your deregistration request based on your
          usage of our products. Unless otherwise provided by laws and
          regulations, we will stop offering you our products and services one
          you deregister your account with us, and we will delete your personal
          information per your request.
        </p>
        <p>5. Exceptions</p>
        <p>
          In accordance with relevant laws and regulations, we will not be able
          to respond to your correction, deletion or deregistration request
          under following circumstances:
        </p>
        <p>
          (1) when we are bound by the obligations under the relevant laws and
          regulations;
        </p>
        <p>
          (2) when it is directly related to national security and national
          defense security;
        </p>
        <p>
          (3) when it is directly related to public security, public health and
          major public interests;
        </p>
        <p>
          (4) when it is directly related to criminal investigation,
          prosecution, trial and judgment execution;
        </p>
        <p>
          (5) if there is sufficient evidence indicating that you are abusing
          your rights;
        </p>
        <p>
          (6) when it is for the purpose of safeguarding your or other’s life,
          property and other major legal rights and interests while it is
          difficult to obtain prior consent;
        </p>
        <p>
          (7) if responding to such request will cause serious damage to your or
          other individuals or organizations’ legal rights and interests; or
        </p>
        <p>(8) if any trade secret is concerned.</p>
        <p>
          <strong>
            V. How your personal information is transferred globally
          </strong>
        </p>
        <p>
          In principle, the personal information we collect and generate in the
          People's Republic of China will be stored in the People's Republic of
          China. As we provide products or services through resources and
          servers globally, after we obtain your authorization and consent, your
          personal information may be transferred to or accessed from foreign
          jurisdictions. Those jurisdictions may have different data protection
          laws or even have no such laws. Under such circumstances, we will
          endeavor that your personal information will be adequately and equally
          protected as in the People's Republic of China.
        </p>
        <p>
          <strong>VI. Changes to Privacy Policy</strong>
        </p>
        <p>
          We may update our Privacy Policy from time to time. Thus, you are
          advised to review this page periodically for any changes. We will
          notify you of any changes by posting the new Privacy Policy on this
          page. These changes are effective immediately after they are posted on
          this page.
        </p>
        <p>
          <strong>VII. Contact us</strong>
        </p>
        <p>
          If you have any questions or suggestions about our Privacy Policy, do
          not hesitate to contact us at info@miotech.com.
        </p>
        <p>Effective as of August 2, 2021</p>
      </div>
    ),
    [],
  );

  const cnZhContent = useMemo(
    () => (
      <div className="policy-content">
        <h2>隐私政策</h2>
        <div style={{ marginBottom: 24 }}>
          <i>更新于2021年8月2日</i>
        </div>
        <p>
          <strong>
            请在使用我们的产品或服务前，仔细阅读并了解本《隐私政策》。
          </strong>
        </p>
        <p>
          <strong>【前言】</strong>
          欢迎您使用妙盈科技的产品和服务。妙盈科技非常重视您的隐私并承诺保护您的个人信息私隐。在您使用我们的产品和服务前及过程中，我们可能会收集和使用您的相关个人信息。我们希望通过本《隐私政策》向您说明我们在收集和使用您的个人信息时对应的处理规则等相关事宜，以便更好的保障您的权益。
        </p>
        <p>
          本《隐私政策》涵盖使用
          <a href="https://www.miotech.com">www.miotech.com</a>
          网站、妙盈科技其他网址和网站（统称“<strong>妙盈网站</strong>
          ”）、AMI、Spreadi等妙盈科技平台和产品以及我们的其它服务（与妙盈网站统称“
          <strong>妙盈产品和服务</strong>”）的用户。
        </p>
        <p>
          在本《隐私政策》中，“<strong>MioTech</strong>”、“
          <strong>妙盈科技</strong>”、“<strong>本公司</strong>”和“
          <strong>我们</strong>
          ”是指颖投信息科技（上海）有限公司及其关联公司，而“
          <strong>用户</strong>”和“<strong>您</strong>
          ”指妙盈产品和服务的每位用户和客户。
        </p>
        <p>
          <strong className="text-underline">
            【特别提示】我们会不时更新本《隐私政策》并在妙盈网站上公布。本《隐私政策》属于所有妙盈产品和服务协议不可分割的一部分。当您访问妙盈网站或使用妙盈产品和服务时，即表示您已经同意了本《隐私政策》的全部内容，否则您应当立即停止访问妙盈网站或使用妙盈产品和服务。我们承诺以安全和透明的方式保护您的个人信息。我们收集个人信息系用于提供和改进产品和服务，除本《隐私政策》另有约定外，不会出于任何其他目的使用或共享您的个人信息。
          </strong>
        </p>
        <p>目录</p>
        <p>本隐私政策将帮助您了解以下内容：</p>
        <p>一、我们如何收集和使用您的个人信息</p>
        <p>二、我们如何储存、委托处理、共享、转让、公开披露您的个人信息</p>
        <p>三、我们如何保护您的个人信息</p>
        <p>四、您的权利</p>
        <p>五、您的个人信息如何在全球范围转移</p>
        <p>六、本隐私政策的变更</p>
        <p>七、如何联系我们</p>
        <br />
        <br />
        <p>
          我们深知个人信息对您的重要性，也深知为您的个人信息提供有效保护是我们业务健康可持续发展的基石。感谢您对妙盈的使用和信任！我们致力于维持您对我们的信任，恪守本隐私政策保护您的个人信息。
        </p>
        <p>我们郑重承诺，我们将采取相应的安全保护措施来保护您的个人信息。</p>
        <p>
          <strong>一、我们如何收集和使用您的个人信息</strong>
        </p>
        <p>（一）我们收集哪些您的个人信息以及收集的方式</p>
        <p>1. 您提供的信息</p>
        <p>
          当您联系我们、订阅我们的新闻通讯、购买妙盈产品和服务、创建帐户，给我们发送电子邮件或以其他任何方式与我们共享个人信息时，我们会收集您共享的信息。
        </p>
        <p>
          这些信息，取决于您的选择，可能包括您的姓名、电话号码、邮件地址、公司名称、职位、公司住址或付款信息等。目前，电话号码/邮箱地址是必须提供的，电话号码/邮箱地址是您注册并登录妙盈网站，使用妙盈产品和服务的一种方式，您需要通过电话号码/邮箱地址注册后才可以继续使用我们的相关服务。用户电话号码/邮箱地址有保证账户安全、接收平台提醒等多种功能。除此之外，其它信息不是必须提供的，但是如果缺失该等信息，您将无法访问我们的某些内容或功能，或不能使用某些服务。
        </p>
        <p>2. 您发布的信息</p>
        <p>您在妙盈网站、妙盈产品和服务的公共区域发布的信息。</p>
        <p>3. 为您提供服务过程中形成的信息</p>
        <p>
          我们以及为妙盈产品和服务提供内容、广告或其他功能的第三方可能会使用Cookie、像素标签、本地存储和其他技术自动在产品和服务过程中收集信息。我们使用的技术主要是配置在您的计算机、平板电脑、移动电话或其他设备上的小型数据文件，其可以在您访问我们的网站、服务、应用程序或与我们互动时记录某些信息，并在各种设备上识别您的身份。大多数浏览器具有阻止和删除Cookie的功能。不过，如果您这样做，服务可能无法正常运行。我们还使用诸如Google
          Analytics等第三方分析服务收集和分析汇总匿名用户信息。
        </p>
        <p>4. 其他来源的信息</p>
        <p>
          我们会从其他来源，包括第三方服务机构和组织，获取您的信息，用以完善您提供的信息。我们可以通过这些信息核实您提供给妙盈科技的信息，提升我们向您提供业务、产品和服务相关信息的能力。
        </p>
        <br />
        <br />
        <p>（二）我们如何使用您的个人信息</p>
        <p>1. 在向您提供我们的产品或服务，并为改进这些产品或服务时使用。</p>
        <p>
          2.
          在我们向您提供产品和服务期间，您授权我们持续收集和使用您的个人信息。在您注销服务时，我们将停止收集您相关的个人信息。
        </p>
        <p>
          3.
          为提升您的产品或服务体验，或为防范风险，我们会对服务使用情况进行汇总、统计分析、加工，但这些信息不会包含您的任何身份识别信息。
        </p>
        <p>
          4.
          我们也会使用上述信息来维护和改进我们的产品和服务功能，开发新的业务功能等。
        </p>
        <p>5. 将您的个人信息用于法律允许的其它用途。</p>
        <p>
          <strong>
            二、我们如何储存、委托处理、共享、转让、公开披露您的个人信息
          </strong>
        </p>
        <p>（一）储存</p>
        <p>1. 我们将遵守相关的法律法规规定的地点储存您的个人信息。</p>
        <p>
          2.
          我们仅在法律法规要求的期限内，以及为实现本隐私政策的目的所必须的期限内，确保单个用户数据的最长储存期限以及用户日志的储存期限。
        </p>
        <p>（二）委托处理、共享</p>
        <p>
          1.
          出于下列原因，我们可能会聘用第三方公司和个人处理和共享您的个人信息：（1）协助我们提供妙盈产品和服务；（2）以我们的名义提供妙盈产品和服务；（3）我们的产品和服务中某些具体的模块或功能由外部供应商提供；（4）帮助我们分析妙盈产品和服务。
        </p>
        <p>
          2.
          上述第三方有权访问您的个人信息，其原因在于他们是代表我们履行受分配的任务。但是，他们具有不得以任何其他目的披露或使用该信息的义务。
        </p>
        <p>
          3.
          我们仅会出于合法、正当、必要、特定、明确的目的共享您的个人信息，并且只会共享提供妙盈产品和服务所必要的个人信息，但经过处理无法识别特定个人且不能复原的除外。
        </p>
        <p>
          4.
          对我们与之共享个人信息的公司、组织和个人，我们会与其签署保密协定，要求他们按照我们的要求以及其他任何相关的保密和安全措施来处理个人信息。
        </p>
        <p>（三）转让</p>
        <p>
          我们不会将您的个人信息转让给除关联公司外的任何公司、组织和个人，但以下情况除外：
        </p>
        <p>1. 本隐私政策明确规定的情形；</p>
        <p>2. 满足法律法规、法律程序的要求或强制性的政府要求或司法裁定；或</p>
        <p>
          3.
          如果我们或我们的关联公司涉及合并、分立、清算、资产或业务的收购或出售等交易，您的个人信息有可能作为此类交易的一部分而被转移，我们将确保该等信息在转移时的机密性，并尽最大可能确保持有您个人信息的新的公司、组织继续受此隐私政策的约束。
        </p>
        <p>（四）披露</p>
        <p>我们仅会在以下情况下，披露您的个人信息：</p>
        <p>1. 本隐私政策明确规定的情形；</p>
        <p>2. 获得您的明确同意；或</p>
        <p>
          3.
          基于法律的披露：在法律、法律程序、诉讼或政府主管部门强制性要求的情况下，我们可能会公开披露您的个人信息。
        </p>
        <p>（五）共享、转让、公开披露个人信息时事先征得授权同意的例外</p>
        <p>
          请注意，下列情形共享、转让、公开披露个人信息无需事先征得您的授权同意：
        </p>
        <p>1. 与国家安全、国防安全直接相关的；</p>
        <p>2. 与公共安全、公共卫生、重大公共利益直接相关的；</p>
        <p>3. 与犯罪侦查、起诉、审判和判决执行等直接相关的；</p>
        <p>
          4.
          出于维护您或其他个人的生命、财产等重大合法权益但又很难得到本人同意的；
        </p>
        <p>5. 您自行向社会公众公开的个人信息；</p>
        <p>
          6.
          从合法公开披露的信息中收集个人信息的，如合法的新闻报道、政府信息公开等渠道。
        </p>
        <p>
          <strong>三、我们如何保护您的个人信息</strong>
        </p>
        <p>
          1.
          我们已使用符合业界标准的安全防护措施保护您提供的个人信息，防止数据遭到未经授权的访问、公开披露、使用、修改、损坏或丢失。我们会采取一切合理可行的措施，保护您的个人信息。
        </p>
        <p>
          2.
          我们重视您基于对我们的信任提供的个人信息，因此我们正尽力使用商业方面可接受的方法来保护您的信息。然而请您理解，由于技术水平局限以及可能存在的恶意攻击，有可能出现我们无法合理预见、防范、避免、控制的意外情况。互联网并非绝对安全的环境，请使用复杂密码，协助我们保证您的账号安全。
        </p>
        <p>
          3.
          我们的服务可能包含跳转至其他网站的链接。如果单击第三方链接，您将直接访问该网站。请注意，这些外部网站并非我们运营。因此，我们强烈建议您浏览这些网站的隐私政策。我们无法控制任何第三方网站或服务的内容、隐私政策或操作，也不对此承担任何责任。
        </p>
        <p>
          4.
          如发生个人信息安全事件，我们将按照法律法规的要求，及时将事件相关情况以邮件、信函、电话、推送通知等方式告知您，或采取合理、有效的方式发布公告。同时，我们还将依据监管规定，上报个人信息安全事件处置情况。
        </p>
        <p>
          5.
          如何处理未成年人的个人信息。我们不向未满13周岁的未成年人提供服务，也不会有意收集未满13周岁未成年人的个人身份信息。如我们发现收到了未满13周岁未成年人提供的个人信息，会立即将其从服务器删除。如果您为未成年人的父母或监护人，且知悉其向我们提供了个人信息，请联系我们，以便我们能够采取必要的措施。
        </p>
        <p>
          <strong>四、您的权利</strong>
        </p>
        <p>
          按照中国相关的法律、法规、标准，以及其他国家、地区的通行做法，我们保障您对自己的个人信息行使以下权利：
        </p>
        <p>（一）访问、更正您的个人信息</p>
        <p>
          您有权访问您的个人信息，法律法规规定的例外情况除外。如果您想行使数据访问权，请通过info@miotech.com联系我们。当您发现我们处理的关于您的个人信息有错误时，您有权要求我们作出更正。您可以通过info@miotech.com联系我们提出更正申请。
        </p>
        <p>（二）改变个人信息授权</p>
        <p>
          您有权拒绝和退出某些使用和披露您个人信息的项目。如果您已经同意妙盈科技处理您的个人信息，您也可以发送电子邮件至info@miotech.com，随时撤销该同意并退出进一步处理。您退出后，我们仍可能收集和使用您在我们网站上的活动和/或第三方网站广告的相关非个人信息，并将之用于非营利性广告目的，譬如判断广告的有效性。
        </p>
        <p>
          如果您不希望再收到我们发送的电子邮件，您可以点击电子邮件底部的取消订阅链接，选择以后不再接收电子邮件。我们会在收到邮件后的合理期限内处理您的请求。请注意，您会继续收到关于订阅的产品或服务交易的电子邮件。我们也会给您发送一些有关妙盈科技及服务的非广告信函，而您将无法退订这些信函（例如，本条款或本隐私政策更新的相关函件）。
        </p>
        <p>（三）删除权</p>
        <p>在以下情形中，您可以向我们提出删除个人信息的请求：</p>
        <p>1. 如果我们终止妙盈网站或妙盈产品和服务的运营及服务；</p>
        <p>2. 如果您注销了帐号，我们会删除您的个人信息或做匿名化处理；</p>
        <p>3. 涉及商业秘密的。</p>
        <p>（四）注销权</p>
        <p>
          您随时可注销此前和我们注册的账户。您可以联系我们的客服人员，注销您的妙盈帐号。一旦您注销账号，将无法使用我们的产品和服务，因此请您谨慎操作。我们为了保护您或他人的合法权益会结合您对我们公司各产品的使用情况判断是否支持您的注销请求。除法律法规另有规定外，注销帐号之后我们将停止为您提供产品和服务，并根据您的要求删除您的个人信息。
        </p>
        <p>（五）例外情形</p>
        <p>
          在以下情形中，按照法律法规要求，我们将无法响应您的更正、删除、注销信息的请求：
        </p>
        <p>1. 由于我们负有相关法律法规规定的义务；</p>
        <p>2. 与国家安全、国防安全直接相关的；</p>
        <p>3. 与公共安全、公共卫生、重大公共利益直接相关的；</p>
        <p>4. 与刑事侦查、起诉、审判和执行判决等直接相关的；</p>
        <p>5. 有充分证据表明您存在滥用权利的；</p>
        <p>
          6.
          出于维护您或其他个人的生命、财产等重大合法权益但又很难得到本人同意的；
        </p>
        <p>7. 响应该等请求将导致您或其他个人、组织的合法权益受到严重损害的；</p>
        <p>8. 涉及商业秘密的。</p>
        <p>
          <strong>五、您的个人信息如何在全球范围转移</strong>
        </p>
        <p>
          原则上，我们在中华人民共和国境内收集和产生的个人信息，将存储在中华人民共和国境内。由于我们通过遍布全球的资源和服务器提供产品或服务，这意味着，在获得您的授权同意后，您的个人信息可能会被转移到您使用产品或服务所在国家/地区的境外管辖区，或者受到来自这些管辖区的访问。此类管辖区可能设有不同的数据保护法，甚至未设立相关法律。在此类情况下，我们会确保您的个人信息得到在中华人民共和国境内足够同等的保护。
        </p>
        <p>
          <strong>六、本隐私政策的变更</strong>
        </p>
        <p>
          我们可能不时对本隐私政策进行修订。因此，建议您定期查看此页面的变更情况。我们将在本页发布新的隐私政策，向您告知任何变更。所有变更在本页发布后立即生效。
        </p>
        <p>
          <strong>七、如何联系我们</strong>
        </p>
        <p>
          如您对我们的《隐私政策》有任何问题或建议，请发送邮件至
          info@miotech.com，与我们联系。
        </p>
        <p>于2021年8月2日生效。</p>
      </div>
    ),
    [],
  );

  const cnHkContent = useMemo(
    () => (
      <div className="policy-content">
        <h2>隱私政策</h2>
        <div style={{ marginBottom: 24 }}>
          <i>更新於2021年8月2日</i>
        </div>
        <p>
          <strong>
            請在使用我們的產品或服務前，仔細閱讀並了解本《隱私政策》。
          </strong>
        </p>
        <p>
          <strong>【前言】</strong>
          歡迎您使用妙盈科技的產品和服務。妙盈科技非常重視您的隱私並承諾保護您的個人信息私隱。在您使用我們的產品和服務前及過程中，我們可能會收集和使用您的相關個人信息。我們希望通過本《隱私政策》向您說明我們在收集和使用您的個人信息時對應的處理規則等相關事宜，以便更好的保障您的權益。
        </p>
        <p>
          本《隱私政策》涵蓋使用
          <a href="https://www.miotech.com">www.miotech.com</a>
          網站、妙盈科技其他網址和網站（統稱“<strong>妙盈網站</strong>
          ”）、AMI、Spreadi等妙盈科技平台和產品以及我們的其它服務（與妙盈網站統稱“
          <strong>妙盈產品和服務</strong>”）的用戶。
        </p>
        <p>
          在本《隱私政策》中，“<strong>MioTech</strong>”、“
          <strong>妙盈科技</strong>”、“<strong>本公司</strong>”和“
          <strong>我們</strong>
          ”是指穎投信息科技（上海）有限公司及其關聯公司，而“
          <strong>用戶</strong>”和“<strong>您</strong>
          ”指妙盈產品和服務的每位用戶和客戶。
        </p>
        <p>
          <strong className="text-underline">
            【特別提示】我們會不時更新本《隱私政策》並在妙盈網站上公佈。本《隱私政策》屬於所有妙盈產品和服務協議不可分割的一部分。當您訪問妙盈網站或使用妙盈產品和服務時，即表示您已經同意了本《隱私政策》的全部內容，否則您應當立即停止訪問妙盈網站或使用妙盈產品和服務。我們承諾以安全和透明的方式保護您的個人信息。我們收集個人信息系用於提供和改進產品和服務，除本《隱私政策》另有約定外，不會出於任何其他目的使用或共享您的個人信息。
          </strong>
        </p>
        <p>目錄</p>
        <p>本隱私政策將幫助您了解以下內容：</p>
        <p>一、我們如何收集和使用您的個人信息</p>
        <p>二、我們如何儲存、委託處理、共享、轉讓、公開披露您的個人信息</p>
        <p>三、我們如何保護您的個人信息</p>
        <p>四、您的權利</p>
        <p>五、您的個人信息如何在全球範圍轉移</p>
        <p>六、本隱私政策的變更</p>
        <p>七、如何联系我們</p>
        <br />
        <br />
        <p>
          我們深知個人信息對您的重要性，也深知為您的個人信息提供有效保護是我們業務健康可持續發展的基石。感謝您對妙盈的使用和信任！我們致力於維持您對我們的信任，恪守本隱私政策保護您的個人信息。
        </p>
        <p>我們鄭重承諾，我們將採取相應的安全保護措施來保護您的個人信息。 </p>
        <p>
          <strong>一、我們如何收集和使用您的個人信息</strong>
        </p>
        <p>（一）我們收集哪些您的個人信息以及收集的方式</p>
        <p>1. 您提供的信息</p>
        <p>
          當您聯繫我們、訂閱我們的新聞通訊、購買妙盈產品和服務、創建帳戶，給我們發送電子郵件或以其他任何方式與我們共享個人信息時，我們會收集您共享的信息。
        </p>
        <p>
          這些信息，取決於您的選擇，可能包括您的姓名、電話號碼、郵件地址、公司名稱、職位、公司住址或付款信息等。目前，電話號碼/郵箱地址是必須提供的，電話號碼/郵箱地址是您註冊並登錄妙盈網站，使用妙盈產品和服務的一種方式，您需要通過電話號碼/郵箱地址註冊後才可以繼續使用我們的相關服務。用戶電話號碼/郵箱地址有保證賬戶安全、接收平台提醒等多種功能。除此之外，其它信息不是必須提供的，但是如果缺失該等信息，您將無法訪問我們的某些內容或功能，或不能使用某些服務。
        </p>
        <p>2. 您發布的信息</p>
        <p>您在妙盈網站、妙盈產品和服務的公共區域發布的信息。 </p>
        <p>3. 為您提供服務過程中形成的信息</p>
        <p>
          我們以及為妙盈產品和服務提供內容、廣告或其他功能的第三方可能會使用Cookie、像素標籤、本地存儲和其他技術自動在產品和服務過程中收集信息。我們使用的技術主要是配置在您的計算機、平板電腦、移動電話或其他設備上的小型數據文件，其可以在您訪問我們的網站、服務、應用程序或與我們互動時記錄某些信息，並在各種設備上識別您的身份。大多數瀏覽器具有阻止和刪除Cookie的功能。不過，如果您這樣做，服務可能無法正常運行。我們還使用諸如Google
          Analytics等第三方分析服務收集和分析匯總匿名用戶信息。
        </p>
        <p>4. 其他來源的信息</p>
        <p>
          我們會從其他來源，包括第三方服務機構和組織，獲取您的信息，用以完善您提供的信息。我們可以通過這些信息核實您提供給妙盈科技的信息，提升我們向您提供業務、產品和服務相關信息的能力。
        </p>
        <br />
        <br />
        <p>（二）我們如何使用您的個人信息</p>
        <p>1. 在向您提供我們的產品或服務，並為改進這些產品或服務時使用。 </p>
        <p>
          2.
          在我們向您提供產品和服務期間，您授權我們持續收集和使用您的個人信息。在您註銷服務時，我們將停止收集您相關的個人信息。
        </p>
        <p>
          3.
          為提升您的產品或服務體驗，或為防範風險，我們會對服務使用情況進行匯總、統計分析、加工，但這些信息不會包含您的任何身份識別信息。
        </p>
        <p>
          4.
          我們也會使用上述信息來維護和改進我們的產品和服務功能，開發新的業務功能等。
        </p>
        <p>5. 將您的個人信息用於法律允許的其它用途。 </p>
        <p>
          <strong>
            二、我們如何儲存、委託處理、共享、轉讓、公開披露您的個人信息
          </strong>
        </p>
        <p>（一）儲存</p>
        <p>1. 我們將遵守相關的法律法規規定的地點儲存您的個人信息。 </p>
        <p>
          2.
          我們僅在法律法規要求的期限內，以及為實現本隱私政策的目的所必須的期限內，確保單個用戶數據的最長儲存期限以及用戶日誌的儲存期限。
        </p>
        <p>（二）委託處理、共享</p>
        <p>
          1.
          出於下列原因，我們可能會聘用第三方公司和個人處理和共享您的個人信息：（1）協助我們提供妙盈產品和服務；（2）以我們的名義提供妙盈產品和服務；（3）我們的產品和服務中某些具體的模塊或功能由外部供應商提供；（4）幫助我們分析妙盈產品和服務。
        </p>
        <p>
          2.
          上述第三方有權訪問您的個人信息，其原因在於他們是代表我們履行受分配的任務。但是，他們具有不得以任何其他目的披露或使用該信息的義務。
        </p>
        <p>
          3.
          我們僅會出於合法、正當、必要、特定、明確的目的共享您的個人信息，並且只會共享提供妙盈產品和服務所必要的個人信息，但經過處理無法識別特定個人且不能複原的除外。
        </p>
        <p>
          4.
          對我們與之共享個人信息的公司、組織和個人，我們會與其簽署保密協定，要求他們按照我們的要求以及其他任何相關的保密和安全措施來處理個人信息。
        </p>
        <p>（三）轉讓</p>
        <p>
          我們不會將您的個人信息轉讓給除關聯公司外的任何公司、組織和個人，但以下情況除外：
        </p>
        <p>1. 本隱私政策明確規定的情形；</p>
        <p>2. 滿足法律法規、法律程序的要求或強制性的政府要求或司法裁定；或</p>
        <p>
          3.
          如果我們或我們的關聯公司涉及合併、分立、清算、資產或業務的收購或出售等交易，您的個人信息有可能作為此類交易的一部分而被轉移，我們將確保該等信息在轉移時的機密性，並儘最大可能確保持有您個人信息的新的公司、組織繼續受此隱私政策的約束。
        </p>
        <p>（四）披露</p>
        <p>我們僅會在以下情況下，披露您的個人信息：</p>
        <p>1. 本隱私政策明確規定的情形；</p>
        <p>2. 獲得您的明確同意；或</p>
        <p>
          3.
          基於法律的披露：在法律、法律程序、訴訟或政府主管部門強制性要求的情況下，我們可能會公開披露您的個人信息。
        </p>
        <p>（五）共享、轉讓、公開披露個人信息時事先徵得授權同意的例外</p>
        <p>
          請注意，下列情形共享、轉讓、公開披露個人信息無需事先徵得您的授權同意：
        </p>
        <p>1. 與國家安全、國防安全直接相關的；</p>
        <p>2. 與公共安全、公共衛生、重大公共利益直接相關的；</p>
        <p>3. 與犯罪偵查、起訴、審判和判決執行等直接相關的；</p>
        <p>
          4.
          出於維護您或其他個人的生命、財產等重大合法權益但又很難得到本人同意的；
        </p>
        <p>5. 您自行向社會公眾公開的個人信息；</p>
        <p>
          6.
          從合法公開披露的信息中收集個人信息的，如合法的新聞報導、政府信息公開等渠道。
        </p>
        <p>
          <strong>三、我們如何保護您的個人信息</strong>
        </p>
        <p>
          1.
          我們已使用符合業界標準的安全防護措施保護您提供的個人信息，防止數據遭到未經授權的訪問、公開披露、使用、修改、損壞或丟失。我們會採取一切合理可行的措施，保護您的個人信息。
        </p>
        <p>
          2.
          我們重視您基於對我們的信任提供的個人信息，因此我們正盡力使用商業方面可接受的方法來保護您的信息。然而請您理解，由於技術水平局限以及可能存在的惡意攻擊，有可能出現我們無法合理預見、防範、避免、控制的意外情況。互聯網並非絕對安全的環境，請使用複雜密碼，協助我們保證您的賬號安全。
        </p>
        <p>
          3.
          我們的服務可能包含跳轉至其他網站的鏈接。如果單擊第三方鏈接，您將直接訪問該網站。請注意，這些外部網站並非我們運營。因此，我們強烈建議您瀏覽這些網站的隱私政策。我們無法控制任何第三方網站或服務的內容、隱私政策或操作，也不對此承擔任何責任。
        </p>
        <p>
          4.
          如發生個人信息安全事件，我們將按照法律法規的要求，及時將事件相關情況以郵件、信函、電話、推送通知等方式告知您，或採取合理、有效的方式發佈公告。同時，我們還將依據監管規定，上報個人信息安全事件處置情況。
        </p>
        <p>
          5.
          如何處理未成年人的個人信息。我們不向未滿13周歲的未成年人提供服務，也不會有意收集未滿13周歲未成年人的個人身份信息。如我們發現收到了未滿13周歲未成年人提供的個人信息，會立即將其從服務器刪除。如果您為未成年人的父母或監護人，且知悉其向我們提供了個人信息，請聯繫我們，以便我們能夠採取必要的措施。
        </p>
        <p>
          <strong>四、您的權利</strong>
        </p>
        <p>
          按照中國相關的法律、法規、標準，以及其他國家、地區的通行做法，我們保障您對自己的個人信息行使以下權利：
        </p>
        <p>（一）訪問、更正您的個人信息</p>
        <p>
          您有權訪問您的個人信息，法律法規規定的例外情況除外。如果您想行使數據訪問權，請通過info@miotech.com聯繫我們。當您發現我們處理的關於您的個人信息有錯誤時，您有權要求我們作出更正。您可以通過info@miotech.com聯繫我們提出更正申請。
        </p>
        <p>（二）改變個人信息授權</p>
        <p>
          您有權拒絕和退出某些使用和披露您個人信息的項目。如果您已經同意妙盈科技處理您的個人信息，您也可以發送電子郵件至info@miotech.com，隨時撤銷該同意並退出進一步處理。您退出後，我們仍可能收集和使用您在我們網站上的活動和/或第三方網站廣告的相關非個人信息，並將之用於非營利性廣告目的，譬如判斷廣告的有效性。
        </p>
        <p>
          如果您不希望再收到我們發送的電子郵件，您可以點擊電子郵件底部的取消訂閱鏈接，選擇以後不再接收電子郵件。我們會在收到郵件後的合理期限內處理您的請求。請注意，您會繼續收到關於訂閱的產品或服務交易的電子郵件。我們也會給您發送一些有關妙盈科技及服務的非廣告信函，而您將無法退訂這些信函（例如，本條款或本隱私政策更新的相關函件）。
        </p>
        <p>（三）刪除權</p>
        <p>在以下情形中，您可以向我們提出刪除個人信息的請求：</p>
        <p>1. 如果我們終止妙盈網站或妙盈產品和服務的運營及服務；</p>
        <p>2. 如果您註銷了帳號，我們會刪除您的個人信息或做匿名化處理；</p>
        <p>3. 涉及商業秘密的。 </p>
        <p>（四）註銷權</p>
        <p>
          您隨時可註銷此前和我們註冊的賬戶。您可以聯繫我們的客服人員，註銷您的妙盈帳號。一旦您註銷賬號，將無法使用我們的產品和服務，因此請您謹慎操作。我們為了保護您或他人的合法權益會結合您對我們公司各產品的使用情況判斷是否支持您的註銷請求。除法律法規另有規定外，註銷帳號之後我們將停止為您提供產品和服務，並根據您的要求刪除您的個人信息。
        </p>
        <p>（五）例外情形</p>
        <p>
          在以下情形中，按照法律法規要求，我們將無法響應您的更正、刪除、註銷信息的請求：
        </p>
        <p>1. 由於我們負有相關法律法規規定的義務；</p>
        <p>2. 與國家安全、國防安全直接相關的；</p>
        <p>3. 與公共安全、公共衛生、重大公共利益直接相關的；</p>
        <p>4. 與刑事偵查、起訴、審判和執行判決等直接相關的；</p>
        <p>5. 有充分證據表明您存在濫用權利的；</p>
        <p>
          6.
          出於維護您或其他個人的生命、財產等重大合法權益但又很難得到本人同意的；
        </p>
        <p>7. 響應該等請求將導致您或其他個人、組織的合法權益受到嚴重損害的；</p>
        <p>8. 涉及商業秘密的。 </p>
        <p>
          <strong>五、您的個人信息如何在全球範圍轉移</strong>
        </p>
        <p>
          原則上，我們在中華人民共和國境內收集和產生的個人信息，將存儲在中華人民共和國境內。由於我們通過遍布全球的資源和服務器提供產品或服務，這意味著，在獲得您的授權同意後，您的個人信息可能會被轉移到您使用產品或服務所在國家/地區的境外管轄區，或者受到來自這些管轄區的訪問。此類管轄區可能設有不同的數據保護法，甚至未設立相關法律。在此類情況下，我們會確保您的個人信息得到在中華人民共和國境內足夠同等的保護。
        </p>
        <p>
          <strong>六、本隱私政策的變更</strong>
        </p>
        <p>
          我們可能不時對本隱私政策進行修訂。因此，建議您定期查看此頁面的變更情況。我們將在本頁發布新的隱私政策，向您告知任何變更。所有變更在本頁發布後立即生效。
        </p>
        <p>
          <strong>七、如何联系我們</strong>
        </p>
        <p>
          如您對我們的《隱私政策》有任何問題或建議，請發送郵件至
          info@miotech.com，與我們聯繫。
        </p>
        <p>於2021年8月2日生效。 </p>
      </div>
    ),
    [],
  );
  const showContent = useMemo(() => {
    if (getLocale().toLowerCase() === 'zh-cn') {
      return cnZhContent;
    }
    if (getLocale().toLowerCase() === 'zh-hk') {
      return cnHkContent;
    }
    return enContent;
  }, [cnHkContent, cnZhContent, enContent]);

  if (!visible) {
    return null;
  }
  return showContent;
}

export default Policy;
